/**
 * Mehr-Standort Phase 4 — Org-weite Smart-Inbox (Hauptsitz).
 *
 * Zeigt je Standort die wichtigsten handlungsrelevanten Zähler aus allen
 * Filialen (ungelesene Nachrichten, verpasste Anrufe, offene/überfällige
 * Aufgaben & Rechnungen). Lesend aggregiert; macht den Handlungsbedarf der
 * ganzen Organisation auf einen Blick sichtbar.
 */
import { useEffect, useState, useCallback } from "react";
import { Inbox, Loader2, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { usePersistentToggle } from "../../lib/usePersistentToggle";

const METRICS = [
    { key: "unread_messages", label: "Nachrichten", tone: "bg-amber-100 text-amber-800" },
    { key: "missed_calls", label: "Verpasste Anrufe", tone: "bg-red-100 text-red-800" },
    { key: "tasks_open", label: "Aufgaben offen", tone: "bg-violet-100 text-violet-800" },
    { key: "tasks_overdue", label: "Aufgaben überfällig", tone: "bg-red-100 text-red-800" },
    { key: "invoices_open", label: "RG offen", tone: "bg-sky-100 text-sky-800" },
    { key: "invoices_overdue", label: "RG überfällig", tone: "bg-red-100 text-red-800" },
];

export default function OrgInboxPanel({ scope = "all" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = usePersistentToggle("org-inbox-open", false);

    const load = useCallback((sc) => {
        setLoading(true);
        api.get("/org/inbox", { params: { scope: sc } })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(scope); }, [scope, load]);

    if (loading && !data) {
        return (
            <div className="mb-6 flex items-center gap-2 text-sm text-gray-500 py-4 justify-center" data-testid="org-inbox-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Lade Org-Inbox…
            </div>
        );
    }
    const rows = data?.locations || [];
    if (rows.length === 0) return null;

    return (
        <div className="mb-6" data-testid="org-inbox-panel">
            <button type="button" onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-gray-700 mb-2"
                data-testid="org-inbox-toggle" aria-expanded={open}>
                <Inbox className="w-3.5 h-3.5" /> Org-weite Smart-Inbox · Handlungsbedarf je Standort
                <span className="ml-1 text-gray-400">({rows.length})</span>
                <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
            <div className="space-y-2">
                {rows.map((loc) => (
                    <div key={loc.dealer_id} className="border border-gray-200 rounded-sm p-2.5 bg-gray-50/40" data-testid={`org-inbox-row-${loc.dealer_id}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                            <span className="font-bold text-[#111827] text-sm">{loc.site_code}</span>
                            {loc.is_self && <span className="text-[#FFCC00]" title="Hauptsitz">★</span>}
                            <span className="text-gray-600 text-xs truncate">{loc.street}</span>
                            <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-gray-500" data-testid={`org-inbox-total-${loc.dealer_id}`}>
                                {loc.total} offen
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {METRICS.map((m) => {
                                const v = loc.counts?.[m.key] || 0;
                                if (!v) return null;
                                return (
                                    <span key={m.key} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${m.tone}`} data-testid={`org-inbox-${loc.dealer_id}-${m.key}`}>
                                        {m.label}: <span className="font-mono-num">{v}</span>
                                    </span>
                                );
                            })}
                            {loc.total === 0 && (
                                <span className="text-[10px] text-gray-600">Kein Handlungsbedarf</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            )}
        </div>
    );
}
